import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Card, Input, Button, Typography, message, Spin, Icon } from 'antd';
import { getData, createData, createDataUnAuth } from '../../util/API';

const { Title, Text } = Typography;
const { TextArea } = Input;

const ConversationalProfiling = () => {
  const history = useHistory();
  const location = useLocation();
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [allQuestions, setAllQuestions] = useState([]);
  const [formData, setFormData] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [answersMap, setAnswersMap] = useState({});
  const [allAnswersMap, setAllAnswersMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const [showThankYou, setShowThankYou] = useState(false);
  const [questionHistory, setQuestionHistory] = useState([]);
  const [userPhone, setUserPhone] = useState('');
  const [userFullName, setUserFullName] = useState('');
  const [isOnboarding, setIsOnboarding] = useState(true);
  
  const [templateId, setTemplateId] = useState();
  const [initialQuestionCode, setInitialQuestionCode] = useState();
  
  const [welcomeFormName, setWelcomeFormName] = useState('');
  const [welcomeFormDescription, setWelcomeFormDescription] = useState('');
  const [fetchingWelcomeInfo, setFetchingWelcomeInfo] = useState(false);
  
  useEffect(() => {
    const parseQueryParams = () => {
      const searchParams = new URLSearchParams(location.search);
      const urlTemplateId = searchParams.get('initial-questionnaire');
      const urlQuestionCode = searchParams.get('initial-question');
      
      if (urlTemplateId) {
        setTemplateId(urlTemplateId);
      }
      
      if (urlQuestionCode) {
        setInitialQuestionCode(urlQuestionCode.toUpperCase());
      }
    };

    parseQueryParams();
  }, [location.search]);
  
  useEffect(() => {
    const fetchWelcomeFormInfo = async () => {
      if (!templateId) {
        return;
      }
      
      setFetchingWelcomeInfo(true);
      try {
        const apiUrl = `/partner/question/?template_id=${templateId}`;        
        const response = await getData(apiUrl);

        if (response.data && response.data.data) {
          const responseData = response.data.data;
          
          const formName = responseData.form_name || responseData.name || '';
          const formDesc = responseData.description || '';
          
          setWelcomeFormName(formName);
          setWelcomeFormDescription(formDesc);
        } else {
          console.warn('No form info found in response');
        }
      } catch (error) {
        console.error('Error fetching welcome form info:', error);        
      } finally {
        setFetchingWelcomeInfo(false);
      }
    };

    fetchWelcomeFormInfo();
  }, [templateId]);

  useEffect(() => {
    checkUserData();
  }, []);

  const checkUserData = () => {
    const storedUuid = localStorage.getItem('user_uuid');
    const storedPhone = localStorage.getItem('user_phone');
    
    if (storedUuid) {
      setIsOnboarding(false);
      if (storedPhone) {
        setUserPhone(storedPhone);
      }
    } else {
      setIsOnboarding(true);
    }
  };

  const extractPhoneFromAnswers = (answersArray) => {
    for (const answer of answersArray) {
      const phoneRegex = /^[0-9]{10}$/;
      if (phoneRegex.test(answer.answer)) {
        return answer.answer;
      }
      
      if (answer.question_code.includes('PHONE') || 
          answer.question_text.toLowerCase().includes('phone') ||
          answer.question_text.toLowerCase().includes('mobile')) {
        return answer.answer;
      }
    }
    return '';
  };

  const handleStartQuestionnaire = () => {
    if (isOnboarding) {
      if (!userFullName.trim()) {
        message.warning('Please enter your full name');
        return;
      }
      if (!userPhone.trim()) {
        message.warning('Please enter your phone number');
        return;
      }
      const phoneRegex = /^[0-9]{10}$/;
      if (!phoneRegex.test(userPhone.trim())) {
        message.warning('Please enter a valid 10-digit phone number');
        return;
      }
    }
    
    setShowWelcome(false);
    fetchQuestion(initialQuestionCode, templateId);
  };
  
  const fetchQuestion = async (questionCode, templateIdParam = null) => {
    setLoading(true);
    try {
      const currentTemplateId = templateIdParam || templateId;
      
      let apiUrl = '/partner/question/';
      const queryParams = [];
      
      if (currentTemplateId) {
        queryParams.push(`template_id=${currentTemplateId}`);
      }
      
      if (questionCode) {
        queryParams.push(`question_code=${questionCode}`);
      }
      
      if (queryParams.length > 0) {
        apiUrl += '?' + queryParams.join('&');
      }
      
      const response = await getData(apiUrl);
      
      if (response.data && response.data.data) {
        const responseData = response.data.data;
        const formInfo = {
          form_id: responseData.form_id,
          form_name: responseData.form_name,
          is_tree_form: responseData.is_tree_form,
          whatsapp_compatible: responseData.whatsapp_compatible
        };
        
        setFormData(formInfo);
        if (formInfo.is_tree_form === false) {
          if (responseData.questionnaire && responseData.questionnaire.length > 0) {
            const questions = responseData.questionnaire.map(rawQuestion => ({
              id: rawQuestion.id,
              text: rawQuestion.text,
              question_code: rawQuestion.question_code,
              type: rawQuestion.answer_type || 'text',
              tips: rawQuestion.tips,
              placeholder: rawQuestion.placeholder,
              required: rawQuestion.is_required || false,
              category: rawQuestion.category || 'General',
              options: rawQuestion.options || [],
              order: rawQuestion.order || 1
            }));
            
            questions.sort((a, b) => a.order - b.order);
            setAllQuestions(questions);
            
            const initialAnswersMap = {};
            questions.forEach(q => {
              initialAnswersMap[q.question_code] = '';
            });
            setAllAnswersMap(initialAnswersMap);
          }
        } else {
          if (responseData.questionnaire && responseData.questionnaire.length > 0) {
            const rawQuestion = responseData.questionnaire[0];
            const question = {
              id: rawQuestion.id,
              text: rawQuestion.text,
              question_code: rawQuestion.question_code,
              type: rawQuestion.answer_type || 'text',
              tips: rawQuestion.tips,
              placeholder: rawQuestion.placeholder,
              required: rawQuestion.is_required || false,
              category: rawQuestion.category || 'General',
              options: rawQuestion.options || []
            };

            setCurrentQuestion(question);        
            setQuestionHistory(prev => [...prev, question]);
          }
        }
        
      } else {
        console.error('Invalid response structure:', response.data);
        message.error(`Question not found for ${currentTemplateId ? `template_id: ${currentTemplateId}` : ''}${currentTemplateId && questionCode ? ', ' : ''}${questionCode ? `question_code: ${questionCode}` : ''}`);
        handleComplete();
      }
    } catch (error) {
      console.error('Error fetching question:', error);
      console.error('Error response:', error.response);
      
      if (error.response?.status === 404) {
        message.error(`Question not found for ${setTemplateId ? `template_id: ${setTemplateId}` : ''}${setTemplateId && questionCode ? ', ' : ''}${questionCode ? `question_code: ${questionCode}` : ''}`);
      } else if (error.response?.status === 500) {
        message.error('Server error occurred while fetching question');
      } else {
        message.error('Failed to load question. Please check your connection.');
      }
      handleComplete();
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelection = async (selectedOption) => {
    if (!currentQuestion) return;
    
    const answerData = {
      question_id: currentQuestion.id,
      question_code: currentQuestion.question_code,
      question_text: currentQuestion.text,
      answer: selectedOption.text,
      option_id: selectedOption.id
    };
    
    const updatedAnswers = [...answers, answerData];
    const updatedAnswersMap = {
      ...answersMap,
      [currentQuestion.question_code]: selectedOption.text
    };
    
    setAnswers(updatedAnswers);
    setAnswersMap(updatedAnswersMap);
    
    setTimeout(() => {
      if (selectedOption.next_question_code) {
        fetchQuestion(selectedOption.next_question_code, templateId);
      } else {
        handleComplete(updatedAnswers, updatedAnswersMap);
      }
    }, 600);
  };
  
  const handleTextAnswer = (textValue) => {
    if (!currentQuestion || !textValue.trim()) {
      message.warning('Please provide an answer before continuing');
      return;
    }

    const answerData = {
      question_id: currentQuestion.id,
      question_code: currentQuestion.question_code,
      question_text: currentQuestion.text,
      answer: textValue,
      option_id: null
    };

    const updatedAnswers = [...answers, answerData];
    const updatedAnswersMap = {
      ...answersMap,
      [currentQuestion.question_code]: textValue
    };

    setAnswers(updatedAnswers);
    setAnswersMap(updatedAnswersMap);
    
    let updatedPhone = userPhone;
    if (!userPhone && (
      currentQuestion.question_code.includes('PHONE') ||
      currentQuestion.text.toLowerCase().includes('phone') ||
      currentQuestion.text.toLowerCase().includes('mobile') ||
      /^[0-9]{10}$/.test(textValue)
    )) {
      updatedPhone = textValue;
      setUserPhone(textValue);
    }
    
    handleComplete(updatedAnswers, updatedAnswersMap, updatedPhone);
  };
  
  const handleSimpleFormSubmit = () => {
    
    const unansweredRequired = allQuestions.filter(q => 
      q.required && (!allAnswersMap[q.question_code] || !allAnswersMap[q.question_code].toString().trim())
    );
    
    if (unansweredRequired.length > 0) {
      message.error(`Please answer all required questions: ${unansweredRequired.map(q => q.text).join(', ')}`);
      return;
    }
    
    const formattedAnswers = allQuestions.map(question => {
      const answer = allAnswersMap[question.question_code] || '';
      return {
        question_id: question.id,
        question_code: question.question_code,
        question_text: question.text,
        answer: answer.toString(),
        option_id: null
      };
    }).filter(answer => answer.answer.trim() !== '');
    
    handleComplete(formattedAnswers, allAnswersMap);
  };

  const handleComplete = (currentAnswers = null, currentAnswersMap = null, currentPhone = null) => {
    const finalAnswers = currentAnswers || answers;
    const finalAnswersMap = currentAnswersMap || answersMap;
    const finalPhone = currentPhone || userPhone;
    
    if (finalAnswers.length === 0) {
      console.error('No answers to submit');
      message.error('Please answer at least one question');
      return;
    }
    
    setCurrentQuestion(null);
    handleSubmit(finalAnswers, finalPhone);
  };

  const handleSubmit = async (finalAnswers = null, finalPhone = null) => {
    setSubmitting(true);
    try {
      const answersToSubmit = finalAnswers || answers;
      let phoneNumber = finalPhone || userPhone;
      
      if (!phoneNumber) {
        phoneNumber = extractPhoneFromAnswers(answersToSubmit);
        if (phoneNumber) {
          setUserPhone(phoneNumber);
        }
      }
      
      const formattedAnswers = answersToSubmit.map(answer => ({
        question_code: answer.question_code,
        answer: answer.answer,
        phone: phoneNumber || '',
        template_id: templateId
      }));
      
      const submissionData = {
        onboarding: isOnboarding,
        template_id: templateId,
        ...(isOnboarding && userFullName && { fullname: userFullName }),
        ...(phoneNumber && { phone: phoneNumber }),
        answers: formattedAnswers
      };
      
      const storedUuid = localStorage.getItem('user_uuid');
      
      if (storedUuid) {
        await createData('/partner/answer_responded/', submissionData);
      } else {
        await createDataUnAuth('/partner/answer_responded/', submissionData);
      }
      
      message.success('Assessment submitted successfully!');
      setShowThankYou(true);
      
      setTimeout(() => {
        const storedUuid = localStorage.getItem('user_uuid');
        if (storedUuid) {
          history.push('/additional-profiling');
        } else {
          console.log('New user - staying on thank you page');
        }
      }, 3000);
      
    } catch (error) {
      console.error('Error submitting questionnaire:', error);
      message.error('Failed to submit questionnaire. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrevious = () => {
    if (questionHistory.length > 1) {
      const newHistory = questionHistory.slice(0, -2);
      setQuestionHistory(newHistory);
      
      const newAnswers = answers.slice(0, -1);
      setAnswers(newAnswers);
      
      const lastAnswer = answers[answers.length - 1];
      if (lastAnswer) {
        const newAnswersMap = { ...answersMap };
        delete newAnswersMap[lastAnswer.question_code];
        setAnswersMap(newAnswersMap);
      }
      
      const previousQuestion = questionHistory[questionHistory.length - 2];
      if (previousQuestion) {
        setCurrentQuestion(previousQuestion);
      }
    }
  };
  
  const renderQuestionInput = (question, isSimpleMode = false) => {
    if (!question) return null;

    const currentAnswer = isSimpleMode 
      ? (allAnswersMap[question.question_code] || '') 
      : (answersMap[question.question_code] || '');
    
    const updateAnswer = (value) => {
      if (isSimpleMode) {
        setAllAnswersMap(prev => ({
          ...prev,
          [question.question_code]: value
        }));
      } else {
        setAnswersMap(prev => ({
          ...prev,
          [question.question_code]: value
        }));
      }
    };
    
    if (question.options && question.options.length > 0) {
      
      if (question.type === 'multi_choice') {
        const selectedOptions = currentAnswer ? currentAnswer.split(',').map(s => s.trim()) : [];
        
        return (
          <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
            {question.options.map((option) => (
              <div
                key={option.id}
                onClick={() => {
                  let newSelectedOptions;
                  if (selectedOptions.includes(option.text)) {
                    newSelectedOptions = selectedOptions.filter(s => s !== option.text);
                  } else {
                    newSelectedOptions = [...selectedOptions, option.text];
                  }
                  const newValue = newSelectedOptions.join(', ');
                  updateAnswer(newValue);
                }}
                style={{
                  padding: '16px 20px',
                  border: selectedOptions.includes(option.text) ? '2px solid #1a73e8' : '1px solid #dadce0',
                  borderRadius: '8px',
                  background: selectedOptions.includes(option.text) ? '#e8f0fe' : '#ffffff',
                  color: selectedOptions.includes(option.text) ? '#1a73e8' : '#3c4043',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontSize: '14px',
                  fontWeight: selectedOptions.includes(option.text) ? '500' : '400',
                  display: 'flex',
                  alignItems: 'center',
                  fontFamily: '"Google Sans", Roboto, sans-serif'
                }}
                onMouseEnter={(e) => {
                  if (!selectedOptions.includes(option.text)) {
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                    e.currentTarget.style.borderColor = '#1a73e8';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!selectedOptions.includes(option.text)) {
                    e.currentTarget.style.backgroundColor = '#ffffff';
                    e.currentTarget.style.borderColor = '#dadce0';
                  }
                }}
              >
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '4px',
                  border: selectedOptions.includes(option.text) ? '2px solid #1a73e8' : '2px solid #dadce0',
                  marginRight: '16px',
                  transition: 'all 0.2s ease',
                  backgroundColor: selectedOptions.includes(option.text) ? '#1a73e8' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {selectedOptions.includes(option.text) && (
                    <Icon type="check" style={{ fontSize: '12px', color: 'white' }} />
                  )}
                </div>
                <span style={{ flex: 1 }}>{option.text}</span>
                {!isSimpleMode && option.next_question_code && (
                  <span style={{ 
                    marginLeft: 'auto', 
                    fontSize: '12px', 
                    color: '#5f6368',
                    fontStyle: 'italic'
                  }}>
                    → {option.next_question_code}
                  </span>
                )}
              </div>
            ))}
          </div>
        );
      }
      
      return (
        <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
          {question.options.map((option) => (
            <div
              key={option.id}
              onClick={() => {
                if (isSimpleMode) {
                  updateAnswer(option.text);
                } else {
                  handleAnswerSelection(option);
                }
              }}
              style={{
                padding: '16px 20px',
                border: currentAnswer === option.text ? '2px solid #1a73e8' : '1px solid #dadce0',
                borderRadius: '8px',
                background: currentAnswer === option.text ? '#e8f0fe' : '#ffffff',
                color: currentAnswer === option.text ? '#1a73e8' : '#3c4043',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontSize: '14px',
                fontWeight: currentAnswer === option.text ? '500' : '400',
                display: 'flex',
                alignItems: 'center',
                fontFamily: '"Google Sans", Roboto, sans-serif'
              }}
              onMouseEnter={(e) => {
                if (currentAnswer !== option.text) {
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                  e.currentTarget.style.borderColor = '#1a73e8';
                }
              }}
              onMouseLeave={(e) => {
                if (currentAnswer !== option.text) {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                  e.currentTarget.style.borderColor = '#dadce0';
                }
              }}
            >
              <div style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                border: currentAnswer === option.text ? '2px solid #1a73e8' : '2px solid #dadce0',
                marginRight: '16px',
                transition: 'all 0.2s ease',
                backgroundColor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {currentAnswer === option.text && (
                  <div style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: '#1a73e8'
                  }} />
                )}
              </div>
              <span style={{ flex: 1 }}>{option.text}</span>
              {!isSimpleMode && option.next_question_code && (
                <span style={{ 
                  marginLeft: 'auto', 
                  fontSize: '12px', 
                  color: '#5f6368',
                  fontStyle: 'italic'
                }}>
                  → {option.next_question_code}
                </span>
              )}
            </div>
          ))}
        </div>
      );
    }
    
    const inputComponent = question.type === 'textarea' ? (
      <TextArea
        placeholder={
          question.text.toLowerCase().includes('phone') || 
          question.question_code.includes('PHONE')
            ? "Enter your phone number..."
            : question.placeholder || "Your answer"
        }
        value={currentAnswer}
        onChange={(e) => updateAnswer(e.target.value)}
        size="large"
        autoSize={{ minRows: 3, maxRows: 6 }}
        style={{ 
          borderRadius: '8px',
          border: '1px solid #dadce0',
          fontSize: '14px',
          padding: '16px',
          boxShadow: 'none',
          transition: 'all 0.2s ease',
          fontFamily: '"Google Sans", Roboto, sans-serif',
          backgroundColor: 'transparent'
        }}
        onFocus={(e) => {
          e.target.style.borderColor = '#1a73e8';
          e.target.style.boxShadow = 'none';
        }}
        onBlur={(e) => {
          e.target.style.borderColor = '#dadce0';
          e.target.style.boxShadow = 'none';
        }}
        onPressEnter={() => {
          if (!isSimpleMode) {
            handleTextAnswer(currentAnswer);
          }
        }}
      />
    ) : (
      <Input
        placeholder={
          question.text.toLowerCase().includes('phone') || 
          question.question_code.includes('PHONE')
            ? "Enter your phone number..."
            : question.placeholder || "Your answer"
        }
        value={currentAnswer}
        onChange={(e) => updateAnswer(e.target.value)}
        size="large"
        type={question.type === 'number' ? 'number' : question.type === 'email' ? 'email' : question.type === 'date' ? 'date' : 'text'}
        style={{ 
          borderRadius: '8px',
          border: '1px solid #dadce0',
          fontSize: '14px',
          padding: '16px',
          height: '56px',
          boxShadow: 'none',
          transition: 'all 0.2s ease',
          fontFamily: '"Google Sans", Roboto, sans-serif',
          backgroundColor: 'transparent'
        }}
        onFocus={(e) => {
          e.target.style.borderColor = '#1a73e8';
          e.target.style.boxShadow = 'none';
        }}
        onBlur={(e) => {
          e.target.style.borderColor = '#dadce0';
          e.target.style.boxShadow = 'none';
        }}
        onPressEnter={() => {
          if (!isSimpleMode) {
            handleTextAnswer(currentAnswer);
          }
        }}
      />
    );

    return (
      <div>
        {inputComponent}
        {!isSimpleMode && (
          <>
            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              <Button
                type="primary"
                onClick={() => handleTextAnswer(currentAnswer)}
                disabled={!currentAnswer.toString().trim()}
                style={{
                  borderRadius: '24px',
                  background: currentAnswer.toString().trim() ? '#1a73e8' : '#cbd5e1',
                  borderColor: currentAnswer.toString().trim() ? '#1a73e8' : '#cbd5e1',
                  height: '48px',
                  padding: '0 32px',
                  fontSize: '14px',
                  fontWeight: '500',
                  fontFamily: '"Google Sans", Roboto, sans-serif'
                }}
              >
                Continue
              </Button>
            </div>
            <Text style={{ 
              fontSize: '13px', 
              color: '#5f6368', 
              marginTop: '8px', 
              display: 'block',
              textAlign: 'center',
              fontFamily: '"Google Sans", Roboto, sans-serif'
            }}>
              Press Enter or click Continue
            </Text>
          </>
        )}
      </div>
    );
  };
  
  const renderAllQuestions = () => {
    return (
      <div style={{ 
        minHeight: '100vh',
        background: '#f9fbff',
        padding: '40px 24px',
        fontFamily: '"Google Sans", Roboto, sans-serif'
      }}>
        <div style={{ maxWidth: '768px', margin: '0 auto' }}>
          {/* Header - Google Forms style */}
          <div style={{ 
            background: 'white',
            borderRadius: '8px',
            marginBottom: '12px',
            border: '1px solid #dadce0',
            overflow: 'hidden'
          }}>
            {/* Purple header bar */}
            <div style={{
              height: '10px',
              background: 'linear-gradient(90deg, #673ab7 0%, #9c27b0 100%)'
            }} />
            
            <div style={{ padding: '40px 24px 24px' }}>
              <Title level={1} style={{ 
                color: '#202124',
                margin: '0 0 8px 0',
                fontSize: '32px',
                fontWeight: '400',
                fontFamily: '"Google Sans", Roboto, sans-serif'
              }}>
                {formData?.form_name || 'Untitled form'}
              </Title>
              
              <Text style={{ 
                color: '#5f6368', 
                fontSize: '14px',
                display: 'block',
                lineHeight: '20px',
                fontFamily: '"Google Sans", Roboto, sans-serif'
              }}>
                {welcomeFormDescription || 'Please fill out this form'}
              </Text>

              <div style={{ 
                marginTop: '24px',
                padding: '12px 16px',
                background: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e8eaed'
              }}>
                <Text style={{ 
                  color: '#5f6368', 
                  fontSize: '12px',
                  fontFamily: '"Google Sans", Roboto, sans-serif'
                }}>
                  Form ID: {templateId} • {allQuestions.length} questions • Required fields are marked with *
                </Text>
              </div>
            </div>
          </div>

          {/* Questions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {allQuestions.map((question, index) => {
              const hasAnswer = allAnswersMap[question.question_code] && allAnswersMap[question.question_code].toString().trim() !== '';
              const isRequired = question.required;
              const showError = isRequired && !hasAnswer;

              return (
                <div
                  key={question.id}
                  style={{
                    background: 'white',
                    borderRadius: '8px',
                    border: showError ? '2px solid #d93025' : '1px solid #dadce0',
                    transition: 'all 0.2s ease',
                    overflow: 'hidden'
                  }}
                >
                  <div style={{ padding: '24px' }}>
                    {/* Question header */}
                    <div style={{ marginBottom: '20px' }}>
                      <Title level={3} style={{ 
                        color: '#202124',
                        margin: '0 0 4px 0',
                        fontSize: '16px',
                        fontWeight: '400',
                        lineHeight: '24px',
                        fontFamily: '"Google Sans", Roboto, sans-serif'
                      }}>
                        <span style={{ marginRight: '8px', color: '#5f6368' }}>
                          {index + 1}.
                        </span>
                        {question.text}
                        {question.required && (
                          <span style={{ color: '#d93025', marginLeft: '4px' }}>*</span>
                        )}
                      </Title>
                      
                      {question.tips && (
                        <Text style={{ 
                          fontSize: '12px', 
                          color: '#5f6368',
                          display: 'block',
                          marginTop: '4px',
                          fontFamily: '"Google Sans", Roboto, sans-serif'
                        }}>
                          {question.tips}
                        </Text>
                      )}
                    </div>

                    {/* Question input */}
                    <div style={{ marginBottom: showError ? '8px' : '0' }}>
                      {renderQuestionInput(question, true)}
                    </div>

                    {/* Error message */}
                    {showError && (
                      <Text style={{ 
                        fontSize: '12px', 
                        color: '#d93025',
                        display: 'block',
                        fontFamily: '"Google Sans", Roboto, sans-serif'
                      }}>
                        This is a required question
                      </Text>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Submit Button - Google Forms style */}
          <div style={{ 
            marginTop: '32px',
            display: 'flex',
            justifyContent: 'flex-start'
          }}>
            <Button
              type="primary"
              size="large"
              onClick={handleSimpleFormSubmit}
              loading={submitting}
              style={{
                height: '40px',
                padding: '0 24px',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '500',
                background: '#1a73e8',
                borderColor: '#1a73e8',
                boxShadow: 'none',
                fontFamily: '"Google Sans", Roboto, sans-serif'
              }}
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </Button>
          </div>

          {/* Footer */}
          <div style={{ 
            marginTop: '40px',
            textAlign: 'center'
          }}>
            <Text style={{ 
              fontSize: '12px', 
              color: '#5f6368',
              fontFamily: '"Google Sans", Roboto, sans-serif'
            }}>
              Never submit passwords through this form.
            </Text>
          </div>
        </div>
      </div>
    );
  };
  
  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh',
        background: '#f9fbff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '"Google Sans", Roboto, sans-serif'
      }}>
        <div style={{ 
          textAlign: 'center',
          background: 'white',
          borderRadius: '12px',
          padding: '48px 32px',
          boxShadow: '0 1px 3px rgba(60,64,67,.3)',
          maxWidth: '320px',
          width: '100%'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            margin: '0 auto 24px',
            borderRadius: '50%',
            background: '#1a73e8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Spin size="large" style={{ color: 'white' }} />
          </div>
          <Title level={3} style={{ 
            color: '#202124',
            margin: '0 0 8px 0',
            fontSize: '18px',
            fontWeight: '400',
            fontFamily: '"Google Sans", Roboto, sans-serif'
          }}>
            Loading your question
          </Title>
          <Text style={{ 
            color: '#5f6368',
            fontSize: '14px',
            fontFamily: '"Google Sans", Roboto, sans-serif'
          }}>
            Please wait while we prepare your assessment
          </Text>
        </div>
      </div>
    );
  }
  
  if (showWelcome) {
    return (
      <div style={{ 
        minHeight: '100vh',
        background: '#f9fbff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        fontFamily: '"Google Sans", Roboto, sans-serif'
      }}>
        <div style={{ 
          maxWidth: '480px',
          width: '100%',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(60,64,67,.3)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1a73e8 0%, #4285f4 100%)',
            padding: '40px 32px',
            textAlign: 'center',
            color: 'white'
          }}>
            <div style={{
              width: '72px',
              height: '72px',
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              backdropFilter: 'blur(10px)'
            }}>
              <Icon type="form" style={{ fontSize: '36px', color: 'white' }} />
            </div>
            
            <Title level={1} style={{ 
              color: 'white',
              margin: '0 0 8px 0',
              fontSize: '28px',
              fontWeight: '400',
              fontFamily: '"Google Sans", Roboto, sans-serif'
            }}>
              {fetchingWelcomeInfo ? (
                <Spin size="small" />
              ) : (
                welcomeFormName || 'Assessment Form'
              )}
            </Title>
            
            <Text style={{ 
              color: 'rgba(255,255,255,0.9)', 
              fontSize: '16px',
              fontFamily: '"Google Sans", Roboto, sans-serif'
            }}>
              {fetchingWelcomeInfo ? (
                'Loading form details...'
              ) : (
                welcomeFormDescription || 'Please complete this interactive assessment'
              )}
            </Text>
          </div>
          
          <div style={{ padding: '32px' }}>
            {isOnboarding && (
              <div style={{ marginBottom: '32px' }}>
                <div style={{ marginBottom: '20px' }}>
                  <Text style={{ 
                    fontSize: '14px', 
                    fontWeight: '500', 
                    color: '#202124',
                    display: 'block',
                    marginBottom: '8px',
                    fontFamily: '"Google Sans", Roboto, sans-serif'
                  }}>
                    Full Name *
                  </Text>
                  <Input
                    placeholder="Enter your full name"
                    value={userFullName}
                    onChange={(e) => setUserFullName(e.target.value)}
                    size="large"
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #dadce0',
                      fontSize: '16px',
                      padding: '16px',
                      height: '56px',
                      fontFamily: '"Google Sans", Roboto, sans-serif'
                    }}
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <Text style={{ 
                    fontSize: '14px', 
                    fontWeight: '500', 
                    color: '#202124',
                    display: 'block',
                    marginBottom: '8px',
                    fontFamily: '"Google Sans", Roboto, sans-serif'
                  }}>
                    Phone Number *
                  </Text>
                  <Input
                    placeholder="Enter your 10-digit phone number"
                    value={userPhone}
                    onChange={(e) => setUserPhone(e.target.value)}
                    size="large"
                    maxLength={10}
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #dadce0',
                      fontSize: '16px',
                      padding: '16px',
                      height: '56px',
                      fontFamily: '"Google Sans", Roboto, sans-serif'
                    }}
                  />
                </div>
              </div>
            )}

            <div style={{ 
              background: '#e8f0fe',
              border: '1px solid #1a73e8',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <Text style={{ 
                color: '#1a73e8', 
                fontSize: '14px', 
                fontWeight: '500',
                fontFamily: '"Google Sans", Roboto, sans-serif'
              }}>
                <Icon type="info-circle" style={{ marginRight: '8px' }} />
                This is an interactive assessment. Questions will appear one at a time based on your responses.
              </Text>
            </div>

            <Button
              type="primary"
              size="large"
              onClick={handleStartQuestionnaire}
              block
              disabled={fetchingWelcomeInfo}
              style={{
                height: '56px',
                borderRadius: '28px',
                fontSize: '16px',
                fontWeight: '500',
                background: '#1a73e8',
                borderColor: '#1a73e8',
                boxShadow: 'none',
                fontFamily: '"Google Sans", Roboto, sans-serif'
              }}
            >
              {fetchingWelcomeInfo ? <Spin size="small" /> : 'Start Assessment'}
            </Button>

            <div style={{ 
              marginTop: '16px',
              textAlign: 'center'
            }}>
              <Text style={{ 
                fontSize: '12px', 
                color: '#5f6368',
                fontFamily: '"Google Sans", Roboto, sans-serif'
              }}>
                Template: {templateId} • Starting: {initialQuestionCode}
              </Text>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  if (showThankYou) {
    return (
      <div style={{ 
        minHeight: '100vh',
        background: '#f9fbff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        fontFamily: '"Google Sans", Roboto, sans-serif'
      }}>
        <div style={{ 
          maxWidth: '420px',
          width: '100%',
          background: 'white',
          borderRadius: '12px',
          padding: '48px 32px',
          textAlign: 'center',
          boxShadow: '0 1px 3px rgba(60,64,67,.3)'
        }}>
          <div style={{ 
            width: '80px', 
            height: '80px', 
            borderRadius: '50%', 
            background: 'linear-gradient(135deg, #00c851 0%, #00a040 100%)',
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <Icon type="check" style={{ fontSize: '40px', color: 'white' }} />
          </div>
          
          <Title level={1} style={{ 
            color: '#202124',
            margin: '0 0 12px 0',
            fontSize: '24px',
            fontWeight: '400',
            fontFamily: '"Google Sans", Roboto, sans-serif'
          }}>
            Thank you!
          </Title>
          
          <Text style={{ 
            fontSize: '16px',
            color: '#5f6368',
            display: 'block',
            marginBottom: '24px',
            lineHeight: '1.5',
            fontFamily: '"Google Sans", Roboto, sans-serif'
          }}>
            Your assessment has been submitted successfully. We'll review your responses and get back to you soon.
          </Text>

          <div style={{ 
            background: '#e8f5e8',
            border: '1px solid #00c851',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '16px'
          }}>
            <Text style={{ 
              color: '#00a040', 
              fontSize: '14px', 
              fontWeight: '500',
              fontFamily: '"Google Sans", Roboto, sans-serif'
            }}>
              Redirecting to dashboard...
            </Text>
          </div>

          {submitting && <Spin />}
        </div>
      </div>
    );
  }
  
  if (formData && formData.is_tree_form === false && allQuestions.length > 0) {
    return renderAllQuestions();
  }
  
  if (!currentQuestion) {
    return (
      <div style={{ 
        minHeight: '100vh',
        background: '#f9fbff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '"Google Sans", Roboto, sans-serif'
      }}>
        <div style={{ 
          textAlign: 'center',
          background: 'white',
          borderRadius: '12px',
          padding: '48px 32px',
          boxShadow: '0 1px 3px rgba(60,64,67,.3)',
          maxWidth: '320px',
          width: '100%'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            margin: '0 auto 24px',
            borderRadius: '50%',
            background: '#1a73e8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Spin size="large" style={{ color: 'white' }} />
          </div>
          <Title level={3} style={{ 
            color: '#202124',
            margin: '0 0 8px 0',
            fontSize: '18px',
            fontWeight: '400',
            fontFamily: '"Google Sans", Roboto, sans-serif'
          }}>
            Processing...
          </Title>
          <Text style={{ 
            color: '#5f6368',
            fontSize: '14px',
            fontFamily: '"Google Sans", Roboto, sans-serif'
          }}>
            Preparing your next question
          </Text>
        </div>
      </div>
    );
  }
  
  return (
    <div style={{ 
      minHeight: '100vh',
      background: '#f9fbff',
      fontFamily: '"Google Sans", Roboto, sans-serif'
    }}>
      {/* Progress Header */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #dadce0',
        padding: '16px 24px',
        position: 'sticky',
        top: '0',
        zIndex: 10
      }}>
        <div style={{ maxWidth: '768px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <Text style={{ 
              color: '#1a73e8',
              fontSize: '14px',
              fontWeight: '500',
              fontFamily: '"Google Sans", Roboto, sans-serif'
            }}>
              {formData?.form_name || 'Interactive Assessment'}
            </Text>
            <div style={{ marginTop: '2px' }}>
              <Text style={{ 
                color: '#5f6368',
                fontSize: '12px',
                fontFamily: '"Google Sans", Roboto, sans-serif'
              }}>
                Question {questionHistory.length} • {answers.length} answered
              </Text>
            </div>
          </div>
          <div style={{
            padding: '6px 12px',
            background: '#e8f0fe',
            borderRadius: '16px',
            border: '1px solid #1a73e8'
          }}>
            <Text style={{ 
              color: '#1a73e8',
              fontSize: '12px',
              fontWeight: '500',
              fontFamily: '"Google Sans", Roboto, sans-serif'
            }}>
              Interactive Mode
            </Text>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ padding: '40px 24px' }}>
        <div style={{ maxWidth: '768px', margin: '0 auto' }}>
          {/* Question Card */}
          <div style={{ 
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(60,64,67,.3)',
            overflow: 'hidden',
            marginBottom: '24px'
          }}>
            {/* Question Header */}
            <div style={{
              background: 'linear-gradient(135deg, #1a73e8 0%, #4285f4 100%)',
              padding: '32px',
              color: 'white'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '12px',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  {questionHistory.length}
                </div>
                <Text style={{ 
                  color: 'rgba(255,255,255,0.8)',
                  fontSize: '14px',
                  fontFamily: '"Google Sans", Roboto, sans-serif'
                }}>
                  {currentQuestion.question_code} • {currentQuestion.category}
                </Text>
              </div>

              <Title level={2} style={{ 
                color: 'white',
                margin: '0',
                fontSize: '24px',
                fontWeight: '400',
                lineHeight: '1.3',
                fontFamily: '"Google Sans", Roboto, sans-serif'
              }}>
                {currentQuestion.text}
              </Title>
            </div>

            {/* Question Content */}
            <div style={{ padding: '32px' }}>
              {currentQuestion.tips && (
                <div style={{ 
                  marginBottom: '24px',
                  padding: '16px',
                  background: '#f8f9fa',
                  border: '1px solid #e8eaed',
                  borderRadius: '8px'
                }}>
                  <Text style={{ 
                    fontSize: '14px', 
                    color: '#5f6368',
                    fontStyle: 'italic',
                    fontFamily: '"Google Sans", Roboto, sans-serif'
                  }}>
                    💡 {currentQuestion.tips}
                  </Text>
                </div>
              )}

              {renderQuestionInput(currentQuestion, false)}
            </div>
          </div>

          {/* Navigation */}
          <div style={{ 
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 1px 3px rgba(60,64,67,.3)',
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center' 
          }}>
            <Button
              size="large"
              onClick={handlePrevious}
              disabled={questionHistory.length <= 1}
              style={{
                height: '48px',
                minWidth: '120px',
                borderRadius: '24px',
                fontSize: '14px',
                fontWeight: '500',
                border: '1px solid #dadce0',
                background: questionHistory.length <= 1 ? '#f8f9fa' : 'white',
                color: questionHistory.length <= 1 ? '#9aa0a6' : '#5f6368',
                fontFamily: '"Google Sans", Roboto, sans-serif'
              }}
              icon={<Icon type="arrow-left" />}
            >
              Previous
            </Button>

            <div style={{ textAlign: 'center', flex: 1, margin: '0 24px' }}>
              <Text style={{ 
                fontSize: '12px', 
                color: '#5f6368',
                fontFamily: '"Google Sans", Roboto, sans-serif'
              }}>
                Template: {templateId}
              </Text>
            </div>

            <Button
              type="primary"
              size="large"
              onClick={handleComplete}
              style={{
                height: '48px',
                minWidth: '120px',
                borderRadius: '24px',
                fontSize: '14px',
                fontWeight: '500',
                background: '#34a853',
                borderColor: '#34a853',
                boxShadow: 'none',
                fontFamily: '"Google Sans", Roboto, sans-serif'
              }}
              icon={<Icon type="check" />}
            >
              Finish
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversationalProfiling;
